import { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  SelectInput,
  useRecordContext,
  useGetList,
  EditButton,
  DeleteWithConfirmButton,
  useTranslate,
  useLocaleState,
} from 'react-admin';
import {
  Chip,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import WebIcon from '@mui/icons-material/Web';
import ChatIcon from '@mui/icons-material/Chat';
import TelegramIcon from '@mui/icons-material/Telegram';
import { ZapyIcon } from '../../components/icons/ZapyIcon';
import QrCodeIcon from '@mui/icons-material/QrCode2';
import { QRCodeSVG } from 'qrcode.react';
import { ChannelType, type Channel, type ChannelConnection } from '../../types/chat.types';
import { useZapyInstance } from '../../hooks/useZapyInstance';

const useChannelFilters = () => {
  const translate = useTranslate();
  return [
    <SelectInput
      source="type_eq"
      label="resources.channel.filters.type"
      choices={[
        { id: ChannelType.ZAPY, name: translate('resources.channel.types.zapy') },
        { id: 'whatsapp_official', name: translate('resources.channel.types.whatsapp') },
        { id: 'instagram', name: translate('resources.channel.types.instagram') },
        { id: 'webchat', name: translate('resources.channel.types.webchat') },
        { id: 'internal', name: translate('resources.channel.types.internal') },
      ]}
      alwaysOn
      key="type"
    />,
  ];
};

const ChannelTypeChip = () => {
  const record = useRecordContext<Channel>();
  const translate = useTranslate();
  if (!record) return null;

  const getIcon = (type: ChannelType) => {
    switch (type) {
      case ChannelType.ZAPY:
        return <ZapyIcon fontSize="small" />;
      case ChannelType.TELEGRAM:
        return <TelegramIcon fontSize="small" />;
      case 'whatsapp_official':
        return <WhatsAppIcon fontSize="small" />;
      case 'instagram':
        return <InstagramIcon fontSize="small" />;
      case 'webchat':
        return <WebIcon fontSize="small" />;
      case 'internal':
        return <ChatIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getColor = (type: ChannelType): 'success' | 'secondary' | 'info' | 'primary' | 'default' => {
    switch (type) {
      case ChannelType.ZAPY:
      case 'whatsapp_official':
        return 'success';
      case ChannelType.TELEGRAM:
        return 'info';
      case 'instagram':
        return 'secondary';
      case 'webchat':
        return 'info';
      case 'internal':
        return 'primary';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      icon={getIcon(record.type)}
      label={translate(`resources.channel.types.${record.type}`)}
      size="small"
      color={getColor(record.type)}
      variant="outlined"
    />
  );
};

const ConnectionStatus = () => {
  const record = useRecordContext<Channel>();
  if (!record) return null;

  switch (record.type) {
    case ChannelType.ZAPY:
      return <ZapyConnectionStatus channelId={record.id} />;
    case ChannelType.TELEGRAM:
      return <TelegramConnectionStatus channelId={record.id} />;
    default:
      return <Box sx={{ width: 100 }}>—</Box>;
  }
};

const ZapyConnectionStatus = ({ channelId }: { channelId: number }) => {
  const translate = useTranslate();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);

  const { data: connections } = useGetList<ChannelConnection>(
    'channel-connection',
    {
      filter: { channelId_eq: channelId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'createdAt', order: 'DESC' },
    },
    { enabled: !!channelId }
  );

  const connection = connections?.[0];
  if (!connection) return <Chip label={translate('resources.channel.zapy.noConnection')} size="small" color="default" />;

  return (
    <>
      <ZapyStatusChip
        channelConnectionId={connection.id}
        onQrClick={() => {
          setSelectedConnectionId(connection.id);
          setQrDialogOpen(true);
        }}
      />
      {selectedConnectionId && (
        <QrCodeDialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          channelConnectionId={selectedConnectionId}
        />
      )}
    </>
  );
};

const TelegramConnectionStatus = ({ channelId }: { channelId: number }) => {
  const translate = useTranslate();

  const { data: connections } = useGetList<ChannelConnection>(
    'channel-connection',
    {
      filter: { channelId_eq: channelId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'createdAt', order: 'DESC' },
    },
    { enabled: !!channelId }
  );

  const connection = connections?.[0];
  if (!connection) return <Chip label={translate('resources.channel.telegram.noConnection')} size="small" color="default" />;

  const status = connection.status ?? 'disconnected';

  return (
    <Chip
      label={translate(`resources.channel.telegram.statuses.${status === 'connected' ? 'connected' : 'disconnected'}`)}
      size="small"
      color={status === 'connected' ? 'success' : 'error'}
      variant="outlined"
    />
  );
};

const ZapyStatusChip = ({
  channelConnectionId,
  onQrClick,
}: {
  channelConnectionId: number;
  onQrClick: () => void;
}) => {
  const translate = useTranslate();
  const { status, loading } = useZapyInstance({ channelConnectionId, pollingEnabled: false });

  if (loading) return <CircularProgress size={16} />;

  const displayStatus = status?.status || 'disconnected';

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Chip
        label={translate(`resources.channel.zapy.statuses.${displayStatus}`)}
        size="small"
        color={
          displayStatus === 'connected' ? 'success' :
          displayStatus === 'pending_qr_code_scan' ? 'warning' :
          displayStatus === 'disconnected' || displayStatus === 'error' ? 'error' :
          'default'
        }
        variant="outlined"
      />
      {displayStatus === 'pending_qr_code_scan' && (
        <Tooltip title={translate('resources.channel.zapy.scanQRCode')}>
          <span>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onQrClick(); }}>
              <QrCodeIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

const QrCodeDialog = ({
  open,
  onClose,
  channelConnectionId,
}: {
  open: boolean;
  onClose: () => void;
  channelConnectionId: number;
}) => {
  const translate = useTranslate();
  const { status, actionLoading, refreshQRCode } = useZapyInstance({ channelConnectionId });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{translate('resources.channel.zapy.scanQRTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate('resources.channel.zapy.scanQRInstructions')}
        </Typography>
        {status?.qrCode ? (
          <Box display="flex" justifyContent="center">
            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
              <QRCodeSVG value={status.qrCode} size={280} />
            </Box>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={refreshQRCode} disabled={actionLoading === 'qr'}>
          {translate('resources.channel.zapy.refreshQR')}
        </Button>
        <Button onClick={onClose}>{translate('ra.action.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const LocalizedDateField = ({ source }: { source: string }) => {
  const [locale] = useLocaleState();
  return (
    <DateField
      source={source}
      locales={locale}
      showTime
      options={{ dateStyle: 'short', timeStyle: 'short' }}
    />
  );
};

export const ChannelList = () => {
  const channelFilters = useChannelFilters();
  const translate = useTranslate();

  return (
    <List
      filters={channelFilters}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
    >
      <Datagrid bulkActionButtons={false} rowClick="show">
        <TextField source="name" label={translate('resources.channel.fields.name')} />
        <ChannelTypeChip label={translate('resources.channel.fields.type')} />
        <ConnectionStatus label={translate('resources.channel.fields.connectionStatus')} />
        <LocalizedDateField source="createdAt" />
        <EditButton />
        <DeleteWithConfirmButton />
      </Datagrid>
    </List>
  );
};
